// Writing workbooks back to the file the user opened, and reading that file again.
//
// Edits go through xlsxSurgery, which changes only the cells asked for and leaves
// every other part of the package byte for byte as Excel wrote it. Round-tripping
// through a spreadsheet library re-emits parts it does not fully model, and Excel
// then offers to repair the file on open.

import { COLUMN_LETTERS } from "./excelStructure";
import { editWorkbookCells } from "./xlsxSurgery";

/** Shown before the app writes into a workbook. */
export const WORKBOOK_REWRITE_NOTE =
  "Only the cells listed above are changed. Everything else in the workbook is left exactly as it is.";

/** True when the browser can write back to a file the user picked. */
export const canWriteInPlace = () =>
  typeof window !== "undefined" &&
  typeof window.showOpenFilePicker === "function" &&
  typeof window.isSecureContext !== "undefined" &&
  window.isSecureContext;

/**
 * Ask for write access while the click that triggered it still counts as user
 * activation. requestPermission() rejects once a modal dialog or a long await
 * has consumed that activation, so callers must call this before either.
 */
export const ensureWritePermission = async (handle) => {
  if (!handle || typeof handle.queryPermission !== "function") return false;

  try {
    if ((await handle.queryPermission({ mode: "readwrite" })) === "granted") {
      return true;
    }
    return (await handle.requestPermission({ mode: "readwrite" })) === "granted";
  } catch (error) {
    return false; // activation already gone, or the browser refused
  }
};

/**
 * Read the file behind a handle as it is saved on disk right now.
 *
 * The File taken when the workbook was opened is a snapshot: it never sees later edits,
 * and Chrome refuses to read it at all once the file has changed. The handle points at
 * the file itself, so asking it again returns the current contents. Read permission
 * normally lasts while the page is open; if it has lapsed, asking again needs user
 * activation, so call this as the first await after a click.
 * @param {FileSystemFileHandle} handle - the handle the workbook was opened with
 * @returns {Promise<File>}
 */
export const readFileFromDisk = async (handle) => {
  if (!handle || typeof handle.getFile !== "function") {
    throw new Error("This workbook cannot be read again from disk. Open it with the Choose file button.");
  }

  if (
    typeof handle.queryPermission === "function" &&
    (await handle.queryPermission({ mode: "read" })) !== "granted" &&
    (await handle.requestPermission({ mode: "read" })) !== "granted"
  ) {
    const error = new Error("Read permission was not granted");
    error.name = "NotAllowedError";
    throw error;
  }

  return handle.getFile();
};

/** The message to show the user when a workbook could not be read again from disk. */
export const describeReadError = (error, fileName) => {
  const name = String((error && error.name) || "");

  if (name === "NotFoundError") {
    return `${fileName} is no longer where it was opened from - it may have been moved, renamed or deleted. Open it again with the Choose file button.`;
  }
  if (name === "NotAllowedError" || name === "SecurityError") {
    return `The browser did not allow ${fileName} to be read again. Open it again with the Choose file button.`;
  }
  if (name === "NotReadableError") {
    return `${fileName} could not be read just now. If Excel is still saving it, wait a moment and try again.`;
  }
  return (error && error.message) || `${fileName} could not be read.`;
};

const ROOM_COLUMN_INDEX = 9; // J - Room Allocated
const TUTOR_COLUMN_INDEX = 4; // E - Lecturer

/** The room a table row saves into column J: blank means "clear the cell". */
const roomToSave = (row) => String(row.room_allocation || row.room_auto_allo || "").trim();

/** The name a table row saves into column E: blank means "leave the cell alone". */
const tutorToSave = (row) => String(row.tutor_name || "");

/**
 * Write everything the app has produced back into the workbook in one pass: the Room
 * Allocated column and the Lecturer column.
 *
 * Both columns are written straight from the table, the same way the room column always
 * was. An earlier version re-read the sheet to work out which names had actually
 * changed and wrote only those - but the re-read runs through FileReader, and any
 * failure there left the save quietly writing rooms only, with the standardised names
 * never reaching the file and nothing saying so. Writing unconditionally has no such
 * silent path: what the table shows is what the sheet gets.
 *
 * Blank names are skipped rather than cleared. An empty room means "no room, remove it
 * from the sheet", but an empty name means the app simply has nothing to say about that
 * row, and clearing a lecturer nobody edited would be destructive.
 *
 * @param {File|Blob} file - the workbook the rows were read from
 * @param {Array} data - parsed rows, after allocation and name standardisation
 * @returns {Promise<{buffer, written, cleared, names}>}
 */
export const buildWorkbookWithAllResults = async (file, data = []) => {
  const roomColumn = COLUMN_LETTERS[ROOM_COLUMN_INDEX];
  const tutorColumn = COLUMN_LETTERS[TUTOR_COLUMN_INDEX];

  const edits = [];
  let written = 0;
  let cleared = 0;
  let names = 0;

  data.forEach((row) => {
    if (row.is_section_header || !row.row_index) return;

    const room = roomToSave(row);
    const roomRef = roomColumn + row.row_index;

    if (room) {
      // Numeric rooms go in as numbers so Excel does not flag "number stored as text";
      // rooms such as "403A" stay text.
      edits.push({ ref: roomRef, value: /^\d+$/.test(room) ? Number(room) : room });
      written++;
    } else {
      // No room in the table, so the sheet should not show one either
      edits.push({ ref: roomRef, value: null });
      cleared++;
    }

    const tutor = tutorToSave(row);
    if (tutor) {
      edits.push({ ref: tutorColumn + row.row_index, value: tutor });
      names++;
    }
  });

  const { buffer } = await editWorkbookCells(file, edits);
  return { buffer, written, cleared, names };
};

/**
 * Count the table's results that the workbook does not hold yet: the rooms and names Save
 * All Results into File would change in it. Anything that reloads the table from the file
 * throws exactly these away, so this is what a warning before such a reload should name.
 *
 * Uses the same roomToSave and tutorToSave as the save itself, so "unsaved" can never mean
 * something different from "what Save would write".
 * @param {Array} data - the table rows
 * @param {Array<Array>} rows - the sheet as readWorkbookRows returns it, index 0 = Excel row 1
 * @returns {{rooms: number, names: number}} rows whose room, and rows whose name, differ
 */
export const countUnsavedResults = (data = [], rows = []) => {
  let rooms = 0;
  let names = 0;

  data.forEach((row) => {
    if (row.is_section_header || !row.row_index) return;
    const sheetRow = rows[row.row_index - 1] || [];

    // String(value || "") is how parseExcelFile reads these cells into the table, so a row
    // nobody touched always compares equal.
    if (roomToSave(row) !== String(sheetRow[ROOM_COLUMN_INDEX] || "").trim()) rooms++;

    const tutor = tutorToSave(row);
    if (tutor && tutor !== String(sheetRow[TUTOR_COLUMN_INDEX] || "")) names++;
  });

  return { rooms, names };
};

/**
 * Overwrite the file the user originally picked.
 * Permission must already be granted - see ensureWritePermission. createWritable
 * fails on its own if it is not, which needs no user activation.
 */
export const overwriteFile = async (handle, buffer) => {
  if (!handle || typeof handle.createWritable !== "function") {
    throw new Error(
      "This file cannot be written to directly. Re-open it with the Choose file button, or use a browser that supports the File System Access API (Chrome or Edge over HTTPS)."
    );
  }

  const writable = await handle.createWritable();
  await writable.write(buffer);
  await writable.close();
};

/**
 * What went wrong with a write, in terms the UI can act on.
 * Excel holding the file open surfaces in several different shapes depending on the
 * browser and the moment it failed, so they are matched together.
 */
export const classifyWriteError = (error) => {
  const name = String((error && error.name) || "");
  const message = String((error && error.message) || "").toLowerCase();

  if (message.includes("state had changed")) return "state-changed";
  if (name === "NotAllowedError" || message.includes("permission denied")) return "permission";
  if (name === "InvalidStateError" || message.includes("used by another process")) return "locked";
  if (message.includes("corrupted zip") || message.includes("end of data reached")) return "corrupt";
  return "unknown";
};

/** The message to show the user for a failed write. */
export const describeWriteError = (error, buttonLabel = "the button") => {
  const kind = classifyWriteError(error);

  if (kind === "state-changed" || kind === "locked" || kind === "permission") {
    return `Please close the opened Excel file, then click ${buttonLabel} again.`;
  }
  if (kind === "corrupt") {
    return (
      "This workbook looks incomplete or damaged, so it could not be read. " +
      "Upload the original file again."
    );
  }
  return (error && error.message) || "The workbook could not be written.";
};

/** Reject a file that was never really there before trying to parse it. */
export const assertReadableWorkbook = (file) => {
  if (!file) throw new Error("The original workbook is no longer open. Upload it again.");
  if (typeof file.size === "number" && file.size === 0) {
    const error = new Error("end of data reached");
    error.name = "EmptyFileError";
    throw error;
  }
};

/**
 * Write the buffer, retrying the one failure that is worth retrying.
 *
 * "state had changed since it was read from disk" means something touched the file
 * between the read and the write, which usually clears on its own. Everything else -
 * a lock, a denied permission - will not improve by trying again, so it is rethrown
 * immediately rather than making the user wait through four attempts.
 */
export const overwriteWithRetry = async (handle, buffer, attempts = 4) => {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      await overwriteFile(handle, buffer);
      return;
    } catch (error) {
      if (classifyWriteError(error) !== "state-changed" || attempt === attempts) throw error;
      await new Promise((resolve) => setTimeout(resolve, 150 * attempt));
    }
  }
};
